import { useState, useEffect, useCallback, useRef } from 'react';
import { C, FONT, calendarDaysAgo } from './adminShared';
import {
  deriveCostsFromLedger, applyDerivedCosts,
  dismissLedgerVendor, undismissLedgerVendor, updateBusinessCost,
} from '../../utils/api';
import { FREQUENCY_LABELS } from './revenueShared';

// ─── RECALCULATE FROM LEDGER ─────────────────────────────────────────────────
// Shows what the ledger suggests and SAVES NOTHING until Ben confirms. The
// derive endpoint is read-only; apply-derived is the only thing that writes.
//
// Three piles, because two things cannot be known from ledger rows alone: a
// yearly subscription appears once in any 12-month window and is
// indistinguishable from a one-off, and a purchase paid in instalments looks
// exactly like a monthly subscription. Confident comes pre-ticked, uncertain
// comes with the reason in plain English, one-offs are listed but never added.

const box = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 10 };

const input = {
  fontFamily: FONT, fontSize: 12, padding: '6px 8px',
  border: `1px solid ${C.border}`, borderRadius: 6, background: '#fff', color: C.text,
  width: '100%', boxSizing: 'border-box',
};

const pill = (on) => ({
  padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, fontFamily: FONT,
  border: `1px solid ${on ? C.heading : C.border}`,
  background: on ? C.heading : C.card, color: on ? '#FFF8F7' : C.accent, cursor: 'pointer',
});

// Whole dollars, like everywhere else on this tab. Amounts here are always AUD
// (the derivation reads an AUD-only ledger), so no conversion is involved.
const money = (n) => (n == null ? '—' : 'A$' + Math.round(Number(n)).toLocaleString('en-AU', { maximumFractionDigits: 0 }));

/** One proposal. Stacked, never a table — a 6-column table cannot fit 375px. */
function ProposalRow({ p, checked, onToggle, onEdit, onDismiss, editable, dismissing }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ ...box, padding: '10px 12px', marginBottom: 8,
      opacity: dismissing ? 0.5 : 1, transition: 'opacity 120ms ease-out' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <input
          type="checkbox" checked={checked} onChange={onToggle}
          style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0, accentColor: C.heading }}
          aria-label={`Include ${p.name}`}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.heading, wordBreak: 'break-word' }}>{p.name}</div>
          <div style={{ fontSize: 11, color: C.accent, marginTop: 3 }}>
            {money(p.amount)} · {FREQUENCY_LABELS[p.frequency] || p.frequency} ·{' '}
            <strong style={{ color: C.text }}>{money(p.monthlyEquivalent)}/mo</strong>
          </div>
          <div style={{ fontSize: 10.5, color: C.accent, marginTop: 3 }}>
            seen across {p.distinctMonths} month{p.distinctMonths === 1 ? '' : 's'} · last seen {p.lastSeen}
            {p.groupLabel ? ` · ${p.groupLabel}` : ''}
          </div>
          {p.reason && (
            <div style={{ fontSize: 11, color: '#92650A', background: C.amberBg,
              border: '1px solid #F5D69A', borderRadius: 6, padding: '4px 8px', marginTop: 6 }}>
              {p.reason}
            </div>
          )}
          {p.priceChanged && (
            <div style={{ fontSize: 10.5, color: C.accent, marginTop: 4 }}>
              latest payment {money(p.latestAmount)} on {p.latestAmountDate}
            </div>
          )}
          {/* NEVER MERGE SILENTLY — if several ledger names became one row, say so. */}
          {p.rawNames && p.rawNames.length > 1 && (
            <div style={{ fontSize: 10.5, color: C.accent, marginTop: 4, fontStyle: 'italic' }}>
              merged from: {p.rawNames.join(' · ')}
            </div>
          )}
          {editable && (
            <>
              <button onClick={() => setOpen(o => !o)} style={{
                marginTop: 6, background: 'none', border: 'none', padding: 0,
                color: C.heading, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: FONT,
              }}>{open ? 'Done' : 'Change amount or frequency'}</button>
              {open && (
                // Correcting a wrong guess in place beats rejecting the row and
                // retyping the whole cost by hand.
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                  <label style={{ fontSize: 10, color: C.accent }}>Amount (AUD)
                    <input type="number" step="0.01" value={p.amount} style={{ ...input, marginTop: 3 }}
                      onChange={e => onEdit({ amount: Number(e.target.value) || 0 })} />
                  </label>
                  <label style={{ fontSize: 10, color: C.accent }}>Frequency
                    <select value={p.frequency} style={{ ...input, marginTop: 3 }}
                      onChange={e => onEdit({ frequency: e.target.value })}>
                      {Object.entries(FREQUENCY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </label>
                </div>
              )}
            </>
          )}
        </div>
        {onDismiss && (
          <button onClick={onDismiss} disabled={dismissing}
            title={dismissing ? 'Hiding…' : 'Never offer this again'}
            style={{ background: 'none', border: 'none',
              color: dismissing ? C.border : C.accent, fontSize: 18,
              cursor: dismissing ? 'default' : 'pointer', lineHeight: 1,
              padding: '0 2px', flexShrink: 0 }}>×</button>
        )}
      </div>
    </div>
  );
}

export function CostRecalculateModal({ onClose, onApplied }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [ticked, setTicked]   = useState({});   // name -> bool
  const [edits, setEdits]     = useState({});   // name -> {amount?, frequency?}
  const [saving, setSaving]   = useState(false);
  const [result, setResult]   = useState(null);
  const [showOneOffs, setShowOneOffs] = useState(false);
  const [showHidden, setShowHidden]   = useState(false);
  // Every server-touching control on this panel reports that it heard the click
  // and refuses a second one while the first is still in flight.
  const [dismissing, setDismissing] = useState(null);   // vendor name
  const [restoring, setRestoring]   = useState(null);   // vendor name
  const [accepting, setAccepting]   = useState(null);   // ledger key
  const [accepted, setAccepted]     = useState({});     // ledger key -> true
  // Synchronous latches. React state lags a render behind, which is long enough
  // for a double tap to send two requests — so the in-flight check reads a ref.
  const busyRef = useRef({ dismiss: false, restore: false, accept: false, confirm: false });

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data: d } = await deriveCostsFromLedger();
      setData(d);
      // Confident is pre-ticked; review is deliberately not — that is the whole
      // difference between the two piles.
      const t = {};
      for (const p of d.confident) t[p.name] = true;
      setTicked(t);
      setEdits({});
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const merged = (p) => ({ ...p, ...(edits[p.name] || {}) });

  const dismiss = async (name) => {
    if (busyRef.current.dismiss) return;
    busyRef.current.dismiss = true;
    setDismissing(name); setError(null);
    try { await dismissLedgerVendor(name); await load(); }
    catch (e) { setError(e.response?.data?.error || e.message); }
    finally { busyRef.current.dismiss = false; setDismissing(null); }
  };
  const restore = async (name) => {
    if (busyRef.current.restore) return;
    busyRef.current.restore = true;
    setRestoring(name); setError(null);
    try { await undismissLedgerVendor(name); await load(); }
    catch (e) { setError(e.response?.data?.error || e.message); }
    finally { busyRef.current.restore = false; setRestoring(null); }
  };

  /**
   * Take the ledger's figure for a row where it disagrees with Ben's.
   * The discrepancy is not stored anywhere — it exists only in the response
   * that produced it — so this is the one moment it can be acted on.
   */
  const acceptDerived = async (d) => {
    if (busyRef.current.accept) return;
    busyRef.current.accept = true;
    setAccepting(d.ledgerKey); setError(null);
    try {
      const row = (result?.costs || []).find(c => c.ledger_key === d.ledgerKey);
      if (!row) throw new Error('row not found');
      await updateBusinessCost(row.id, { amount: d.derivedAmount });
      setAccepted(a => ({ ...a, [d.ledgerKey]: true }));
      onApplied?.();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      busyRef.current.accept = false;
      setAccepting(null);
    }
  };

  const confirm = async () => {
    if (busyRef.current.confirm) return;   // one confirm, one write
    busyRef.current.confirm = true;
    setSaving(true); setError(null);
    try {
      const chosen = [...data.confident, ...data.review]
        .filter(p => ticked[p.name])
        .map(p => {
          const m = merged(p);
          return {
            name: m.name, amount: m.amount, currency: m.currency || 'AUD',
            frequency: m.frequency, group_label: m.groupLabel, ledger_key: m.name,
          };
        });
      const { data: res } = await applyDerivedCosts(chosen);
      setResult(res);
      onApplied?.();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      busyRef.current.confirm = false;
      setSaving(false);
    }
  };

  // Days since the ledger's last row, on the VIEWER'S calendar (the row date
  // is a plain date, read as that day in the viewer's zone). Falls back to
  // the server's figure only if the date is missing.
  const latestRowDate = data?.ledger?.latestRowDate;
  const stale = latestRowDate && /^\d{4}-\d{2}-\d{2}$/.test(latestRowDate)
    ? calendarDaysAgo(new Date(`${latestRowDate}T12:00:00`))
    : data?.ledger?.daysSinceLatestRow;
  const veryStale = stale != null && stale > 30;
  const chosenCount = [...(data?.confident || []), ...(data?.review || [])].filter(p => ticked[p.name]).length;

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(42,20,0,0.45)', zIndex: 1000,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '16px 10px' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: C.bg, borderRadius: 14, width: '100%', maxWidth: 640,
          border: `1px solid ${C.border}`, padding: '18px 16px 22px', boxSizing: 'border-box' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontFamily: "'Forum', serif", fontSize: '1.15rem', color: C.heading, letterSpacing: '0.03em' }}>
              Recalculate from ledger
            </h3>
            <div style={{ fontSize: 11, color: C.accent, marginTop: 3 }}>
              Nothing is saved until you press Confirm.
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.accent,
            fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>
        </div>

        {/* ── HOW OLD IS THIS PICTURE ───────────────────────────────────────
            Ben updates the ledger by hand and it runs behind. Everything below
            describes the ledger as it stands, not the business as it stands. */}
        {data?.ledger && (
          <div style={{
            marginTop: 14, padding: '10px 12px', borderRadius: 8, fontSize: 11.5,
            background: veryStale ? C.redBg : C.greenBg,
            border: `1px solid ${veryStale ? '#F0C4BC' : '#BFE3D0'}`,
            color: veryStale ? C.red : C.green, fontWeight: veryStale ? 700 : 500,
          }}>
            {veryStale ? '⚠ ' : ''}Ledger last updated {data.ledger.latestRowDate} — {stale} day{stale === 1 ? '' : 's'} ago.
            {veryStale && ' These figures are out of date. Update money.benmilston.com before trusting them.'}
          </div>
        )}

        {error && (
          <div style={{ marginTop: 12, background: C.redBg, border: '1px solid #F0C4BC', borderRadius: 8,
            padding: '10px 12px', fontSize: 12, color: C.red }}>{error}</div>
        )}

        {loading && <div style={{ padding: '32px 0', textAlign: 'center', fontSize: 12, color: C.accent }}>Reading the ledger…</div>}

        {/* ── AFTER CONFIRMING ──────────────────────────────────────────────── */}
        {result && (
          <div style={{ marginTop: 14, ...box, padding: '14px 14px', background: C.greenBg, borderColor: '#BFE3D0' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.green }}>
              Saved — {result.inserted} cost{result.inserted === 1 ? '' : 's'} written, {result.deactivated} replaced.
            </div>
            <div style={{ fontSize: 11.5, color: C.text, marginTop: 6 }}>
              {result.preservedOverrides} row{result.preservedOverrides === 1 ? '' : 's'} kept your own edits.
            </div>
            {result.amountDiscrepancyCount > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: '#92650A' }}>
                  {result.amountDiscrepancyCount} row{result.amountDiscrepancyCount === 1 ? '' : 's'} where the ledger now
                  shows a different amount — your figure was kept:
                </div>
                {result.amountDiscrepancies.map(d => (
                  <div key={d.ledgerKey} style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 11, color: C.text, marginTop: 6 }}>
                    <span>
                      <strong>{d.ledgerKey}</strong> — yours {money(d.keptAmount)}, ledger {money(d.derivedAmount)}
                    </span>
                    {accepted[d.ledgerKey]
                      ? <span style={{ fontSize: 10.5, color: C.green, fontWeight: 700 }}>
                          ledger figure taken
                        </span>
                      : <button onClick={() => acceptDerived(d)} disabled={accepting === d.ledgerKey}
                          style={{ ...pill(false), padding: '4px 10px', fontSize: 10.5,
                            opacity: accepting === d.ledgerKey ? 0.6 : 1,
                            cursor: accepting === d.ledgerKey ? 'default' : 'pointer' }}>
                          {accepting === d.ledgerKey ? 'Taking…' : `Use ${money(d.derivedAmount)}`}
                        </button>}
                  </div>
                ))}
                {/* Not stored anywhere — it comes back in this response only. */}
                <div style={{ fontSize: 10, color: C.accent, marginTop: 6, fontStyle: 'italic' }}>
                  This notice is not saved. It will not be here next time you open this screen.
                </div>
              </div>
            )}
            <button onClick={onClose} style={{ ...pill(true), marginTop: 12 }}>Close</button>
          </div>
        )}

        {data && !result && (
          <>
            {/* ── CONFIDENT ────────────────────────────────────────────────── */}
            <div style={{ marginTop: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: C.heading, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                Confident ({data.confident.length})
              </div>
              <div style={{ fontSize: 11, color: C.accent, margin: '3px 0 10px' }}>
                Repeats on a steady interval at a steady price. Ticked — untick anything you do not want.
              </div>
              {data.confident.length === 0
                ? <div style={{ fontSize: 11.5, color: C.accent, padding: '8px 0' }}>Nothing qualified.</div>
                : data.confident.map(p => (
                    <ProposalRow key={p.name} p={merged(p)} checked={!!ticked[p.name]}
                      onToggle={() => setTicked(t => ({ ...t, [p.name]: !t[p.name] }))}
                      onEdit={(patch) => setEdits(e => ({ ...e, [p.name]: { ...(e[p.name] || {}), ...patch } }))}
                      onDismiss={() => dismiss(p.name)} editable
                      dismissing={dismissing === p.name} />
                  ))}
            </div>

            {/* ── NEEDS YOUR EYE ───────────────────────────────────────────── */}
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: C.heading, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                Needs your eye ({data.review.length})
              </div>
              <div style={{ fontSize: 11, color: C.accent, margin: '3px 0 10px' }}>
                Nothing here is ticked. Tick to include, × to never offer it again. Correct a wrong guess before ticking.
              </div>
              {data.review.length === 0
                ? <div style={{ fontSize: 11.5, color: C.accent, padding: '8px 0' }}>Nothing to review.</div>
                : data.review.map(p => (
                    <ProposalRow key={p.name} p={merged(p)} checked={!!ticked[p.name]}
                      onToggle={() => setTicked(t => ({ ...t, [p.name]: !t[p.name] }))}
                      onEdit={(patch) => setEdits(e => ({ ...e, [p.name]: { ...(e[p.name] || {}), ...patch } }))}
                      onDismiss={() => dismiss(p.name)} editable
                      dismissing={dismissing === p.name} />
                  ))}
            </div>

            {/* ── HIDDEN ───────────────────────────────────────────────────── */}
            {data.dismissed?.length > 0 && (
              <div style={{ marginTop: 14, ...box, padding: '10px 12px' }}>
                <button onClick={() => setShowHidden(s => !s)} style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: FONT,
                  fontSize: 11.5, fontWeight: 700, color: C.heading,
                }}>
                  {data.dismissed.length} hidden {showHidden ? '▾' : '▸'}
                </button>
                {showHidden && data.dismissed.map(n => (
                  <div key={n} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    gap: 10, marginTop: 8, fontSize: 11.5, color: C.text }}>
                    <span style={{ wordBreak: 'break-word' }}>{n}</span>
                    <button onClick={() => restore(n)} disabled={restoring === n}
                      style={{ ...pill(false), padding: '4px 10px', fontSize: 11,
                        opacity: restoring === n ? 0.6 : 1,
                        cursor: restoring === n ? 'default' : 'pointer' }}>
                      {restoring === n ? 'Restoring…' : 'Restore'}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* ── ONE-OFFS ─────────────────────────────────────────────────── */}
            <div style={{ marginTop: 14, ...box, padding: '10px 12px' }}>
              <button onClick={() => setShowOneOffs(s => !s)} style={{
                background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: FONT,
                fontSize: 11.5, fontWeight: 700, color: C.heading,
              }}>
                One-offs and lapsed ({data.oneOffs.length}) {showOneOffs ? '▾' : '▸'}
              </button>
              <div style={{ fontSize: 10.5, color: C.accent, marginTop: 3 }}>Never added. Shown so nothing is dropped silently.</div>
              {showOneOffs && data.oneOffs.map(p => (
                <div key={p.name} style={{ fontSize: 11, color: C.text, marginTop: 7, borderTop: `1px solid ${C.border}`, paddingTop: 7 }}>
                  <strong style={{ wordBreak: 'break-word' }}>{p.name}</strong> — {money(p.amount)} · last {p.lastSeen}
                  <div style={{ color: C.accent, fontSize: 10.5 }}>{p.reason}</div>
                </div>
              ))}
            </div>

            {/* ── CONFIRM ──────────────────────────────────────────────────── */}
            <div style={{ marginTop: 18, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={confirm} disabled={saving || chosenCount === 0} style={{
                ...pill(true), opacity: saving || chosenCount === 0 ? 0.5 : 1,
                cursor: saving || chosenCount === 0 ? 'not-allowed' : 'pointer',
              }}>
                {saving ? 'Saving…' : `Confirm ${chosenCount} cost${chosenCount === 1 ? '' : 's'}`}
              </button>
              <button onClick={onClose} style={pill(false)}>Cancel</button>
              <span style={{ fontSize: 10.5, color: C.accent }}>
                Replaces ledger-sourced costs. Your hand-added ones are untouched.
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
